import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useLogger } from 'vue-logger-plugin'
import { GetBeeNumberAuthenicatedUser } from '../stores/SettingsStore'
import { CustomerOrderGetList, GetOrderDetail } from '../services/OrderService'
import type { IOrderListItem, IOrderDetailGetResponse } from '../types/OrderTypes'
import { FormatCurrency } from '../services/currencyFormatter'
import { formatDateWithShortMonth } from '../api/Common'

export function useOrderHistory() {
  const { t } = useI18n()
  const logger = useLogger()

  // State
  const orders = ref<IOrderListItem[]>([])
  const isLoading = ref<boolean>(false)
  const error = ref<string>('')
  const orderDetails = ref<Map<string, IOrderDetailGetResponse>>(new Map())
  const loadingDetails = ref<Set<string>>(new Set())
  const detailErrors = ref<Map<string, string>>(new Map())

  // Load order history
  async function loadOrderHistory() {
    try {
      isLoading.value = true
      error.value = ''

      const beeNumber = await GetBeeNumberAuthenicatedUser()
      if (!beeNumber) {
        logger.error('No authenticated user bee number found for order history')
        error.value = t('accountInfo.orderHistory.loadError')
        return
      }

      logger.debug('Loading order history for bee number:', beeNumber)
      const response = await CustomerOrderGetList()

      if (response?.items) {
        // Sort orders by orderTime descending (most recent first)
        orders.value = response.items.sort((a, b) => {
          const dateA = new Date(a.orderTime)
          const dateB = new Date(b.orderTime)
          return dateB.getTime() - dateA.getTime()
        })
        logger.debug('Order history loaded and sorted:', orders.value.length, 'orders')
      } else {
        logger.warn('No order data found in response')
        orders.value = []
      }
    } catch (err) {
      console.error('Error loading order history:', err)
      error.value = t('accountInfo.orderHistory.errorLoading')
    } finally {
      isLoading.value = false
    }
  }

  // Format currency using standard culture-aware formatter
  function formatCurrency(amount: number, currencyId: string | null = null): string {
    const formatted = FormatCurrency(amount, currencyId)
    return formatted || String(amount)
  }

  // Format date using standard locale-aware formatter
  function formatDate(dateString: string): string {
    try {
      return formatDateWithShortMonth(dateString)
    } catch {
      return dateString
    }
  }

  // Load detailed order information
  async function loadOrderDetails(aoeOrderIdToken: string): Promise<IOrderDetailGetResponse | null> {
    try {
      // Check if already loading this order
      if (loadingDetails.value.has(aoeOrderIdToken)) {
        return null
      }

      // Check if already cached
      const cached = orderDetails.value.get(aoeOrderIdToken)
      if (cached) {
        return cached
      }

      // Clear any previous error for this order
      detailErrors.value.delete(aoeOrderIdToken)
      loadingDetails.value.add(aoeOrderIdToken)
      logger.debug('Loading order details for:', aoeOrderIdToken)

      const response = await GetOrderDetail(aoeOrderIdToken)

      if (response) {
        orderDetails.value.set(aoeOrderIdToken, response)
        logger.debug('Order details loaded successfully')
        return response
      } else {
        const errorMsg = 'No order detail data found in response'
        logger.warn(errorMsg)
        detailErrors.value.set(aoeOrderIdToken, errorMsg)
        return null
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to load order details'
      console.error('Error loading order details:', err)
      detailErrors.value.set(aoeOrderIdToken, errorMsg)
      return null
    } finally {
      loadingDetails.value.delete(aoeOrderIdToken)
    }
  }

  return {
    // State
    orders,
    isLoading,
    error,
    orderDetails,
    loadingDetails,
    detailErrors,

    // Methods
    loadOrderHistory,
    loadOrderDetails,
    formatCurrency,
    formatDate
  }
}
